import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { api, type YoutubeFunnelSummaryRow } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { InfoTip } from "@/components/ui/tooltip";
import StatsExplainer from "@/components/StatsExplainer";
import { cn } from "@/lib/utils";

const DAY_OPTIONS = [
  { id: 7, label: "7 dni" },
  { id: 14, label: "14 dni" },
  { id: 30, label: "30 dni" },
] as const;

const DEFAULT_LANDING = "https://kasiazareba.pl/pakiet-ebookow.html";

function looksLikeYoutubeUrl(url: string): boolean {
  const raw = url.trim().toLowerCase();
  if (!raw.startsWith("http://") && !raw.startsWith("https://")) return false;
  try {
    const host = new URL(raw).hostname.replace(/^www\./, "");
    return host === "youtube.com" || host === "m.youtube.com" || host === "youtu.be";
  } catch {
    return false;
  }
}

function pct(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return "-";
  return `${Math.round(v * 1000) / 10}%`;
}

async function copyText(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const el = document.createElement("textarea");
    el.value = text;
    document.body.appendChild(el);
    el.select();
    document.execCommand("copy");
    document.body.removeChild(el);
  }
}

function CopyLink({ label, url }: { label: string; url: string }) {
  const [done, setDone] = useState(false);
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      className="h-7 text-xs"
      onClick={async () => {
        await copyText(url);
        setDone(true);
        window.setTimeout(() => setDone(false), 1200);
      }}
    >
      {done ? "Skopiowano" : label}
    </Button>
  );
}

function FunnelRow({ row }: { row: YoutubeFunnelSummaryRow }) {
  const links = row.links;
  const from = row.clicks.by_from || {};
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium">
          {row.title || row.id}
          <span className="ml-2 text-xs font-normal text-muted-foreground">
            via={row.via} · id={row.id}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3">
        <div className="flex flex-wrap gap-2">
          {links?.y_ig && <CopyLink label="IG / ManyChat → YT" url={links.y_ig} />}
          {links?.y_fb && <CopyLink label="FB → YT" url={links.y_fb} />}
          {links?.y_story && <CopyLink label="Story → YT" url={links.y_story} />}
          {links?.v && <CopyLink label="YT → landing" url={links.v} />}
        </div>
        <p className="truncate text-xs text-muted-foreground">
          YT: {row.youtube_url}
        </p>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead>
              <tr className="border-b text-xs text-muted-foreground">
                <th className="py-1 pr-3 font-medium">
                  Kliknięcia → YT
                  <InfoTip tipId="yt_funnel_clicks" className="ml-1 inline" />
                </th>
                <th className="py-1 pr-3 font-medium">
                  Sesje
                  <InfoTip tipId="yt_funnel_sessions" className="ml-1 inline" />
                </th>
                <th className="py-1 pr-3 font-medium">CTA</th>
                <th className="py-1 pr-3 font-medium">Koszyk</th>
                <th className="py-1 pr-3 font-medium">Zakup</th>
                <th className="py-1 pr-3 font-medium">CVR klik→sesja</th>
                <th className="py-1 font-medium">CVR sesja→zakup</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="py-2 pr-3">
                  <span className="font-medium">{row.clicks.total}</span>
                  <span className="ml-2 text-xs text-muted-foreground">
                    IG/ManyChat {from.ig || 0} · FB {from.fb || 0} · TT {from.tt || 0} · story{" "}
                    {from.story || 0}
                  </span>
                </td>
                <td className="py-2 pr-3">{row.landing.sessions}</td>
                <td className="py-2 pr-3">{row.landing.cta}</td>
                <td className="py-2 pr-3">{row.landing.checkout}</td>
                <td className="py-2 pr-3">{row.landing.purchase}</td>
                <td className="py-2 pr-3">{pct(row.cvr.click_to_session)}</td>
                <td className="py-2">{pct(row.cvr.session_to_purchase)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

export default function YoutubeFunnelPanel() {
  const qc = useQueryClient();
  const [days, setDays] = useState<7 | 14 | 30>(7);
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [title, setTitle] = useState("");
  const [titleLoading, setTitleLoading] = useState(false);
  const [titleManual, setTitleManual] = useState(false);
  const [landingUrl, setLandingUrl] = useState(DEFAULT_LANDING);
  const [campaign, setCampaign] = useState("");
  const [preferredId, setPreferredId] = useState("");
  const [formError, setFormError] = useState("");

  const catalog = useQuery({
    queryKey: ["landing-preview-catalog"],
    queryFn: api.landingPreviewCatalog,
    staleTime: 60_000,
    retry: false,
  });

  const landingOptions = useMemo(() => {
    const pages = catalog.data?.pages ?? [];
    return pages.map((p) => ({
      value: p.live_url,
      label: `${p.label} (${p.live_url.replace(/^https?:\/\//, "")})`,
    }));
  }, [catalog.data?.pages]);

  useEffect(() => {
    const url = youtubeUrl.trim();
    if (!looksLikeYoutubeUrl(url) || titleManual) return;
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setTitleLoading(true);
      try {
        const res = await api.youtubeFunnelResolveTitle(url);
        if (!cancelled && res.title) setTitle(res.title);
      } catch {
        /* zostaw puste; create i tak dociągnie na serwerze */
      } finally {
        if (!cancelled) setTitleLoading(false);
      }
    }, 450);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [youtubeUrl, titleManual]);

  const summary = useQuery({
    queryKey: ["youtube-funnel-summary", days],
    queryFn: () => api.youtubeFunnelSummary(days),
    retry: false,
  });

  const create = useMutation({
    mutationFn: api.youtubeFunnelCreate,
    onSuccess: async () => {
      setYoutubeUrl("");
      setTitle("");
      setTitleManual(false);
      setCampaign("");
      setPreferredId("");
      setFormError("");
      await qc.invalidateQueries({ queryKey: ["youtube-funnel-summary"] });
    },
    onError: (err: Error) => {
      setFormError(err.message || "Nie udało się dodać filmu");
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError("");
    if (!youtubeUrl.trim()) {
      setFormError("Podaj URL filmu YouTube");
      return;
    }
    create.mutate({
      youtube_url: youtubeUrl.trim(),
      title: title.trim(),
      landing_url: landingUrl.trim(),
      utm_campaign: campaign.trim(),
      preferred_id: preferredId.trim(),
    });
  }

  const rows = summary.data?.rows ?? [];
  const totals = summary.data?.totals;

  return (
    <div className="grid gap-5">
      <StatsExplainer variant="lejki" />
      <p className="text-sm text-muted-foreground">
        Atrybucja <strong>treści</strong> (jeden film = jeden via), nie tej samej
        osoby między IG a YT.{" "}
        <InfoTip tipId="yt_funnel_overview" />
      </p>

      <div className="flex flex-wrap items-center gap-2">
        {DAY_OPTIONS.map((opt) => (
          <Button
            key={opt.id}
            size="sm"
            variant={opt.id === days ? "default" : "outline"}
            onClick={() => setDays(opt.id)}
          >
            {opt.label}
          </Button>
        ))}
        {totals && (
          <span className="ml-2 text-xs text-muted-foreground">
            {totals.videos} filmów · {totals.clicks} klików → YT · {totals.sessions}{" "}
            sesji · {totals.purchases} zakupów
          </span>
        )}
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Dodaj film do lejka</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="grid gap-3 md:grid-cols-2">
            <label className="grid gap-1 text-sm">
              URL YouTube
              <Input
                value={youtubeUrl}
                onChange={(e) => {
                  setYoutubeUrl(e.target.value);
                  setTitleManual(false);
                }}
                placeholder="https://www.youtube.com/watch?v=…"
                required
              />
            </label>
            <label className="grid gap-1 text-sm">
              Tytuł
              <span className="text-xs font-normal text-muted-foreground">
                {titleLoading
                  ? "pobieram z YouTube…"
                  : "auto z linku (możesz poprawić)"}
              </span>
              <Input
                value={title}
                onChange={(e) => {
                  setTitleManual(true);
                  setTitle(e.target.value);
                }}
                placeholder={titleLoading ? "Pobieranie…" : "wklej URL, tytuł uzupełni się sam"}
              />
            </label>
            <label className="grid gap-1 text-sm">
              Landing
              <Select
                aria-label="Landing"
                value={
                  landingOptions.some((o) => o.value === landingUrl)
                    ? landingUrl
                    : landingOptions[0]?.value || DEFAULT_LANDING
                }
                onChange={setLandingUrl}
                options={
                  landingOptions.length
                    ? landingOptions
                    : [{ value: DEFAULT_LANDING, label: "Pakiet ebooków" }]
                }
              />
            </label>
            <label className="grid gap-1 text-sm">
              utm_campaign
              <Input
                value={campaign}
                onChange={(e) => setCampaign(e.target.value)}
                placeholder="2026-w37-granice"
              />
            </label>
            <label className="grid gap-1 text-sm md:col-span-2">
              Opcjonalny krótki id (bez yt_)
              <Input
                value={preferredId}
                onChange={(e) => setPreferredId(e.target.value)}
                placeholder="w37granice"
              />
            </label>
            {formError && (
              <p className="text-sm text-destructive md:col-span-2">{formError}</p>
            )}
            <div className="md:col-span-2">
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? "Zapis…" : "Dodaj film"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {summary.isLoading && (
        <p className="text-sm text-muted-foreground">Ładowanie lejka YT…</p>
      )}
      {summary.isError && (
        <p className="text-sm text-destructive">
          Nie udało się pobrać podsumowania lejka YT.
        </p>
      )}
      {!summary.isLoading && rows.length === 0 && (
        <p className={cn("text-sm text-muted-foreground")}>
          Brak filmów. Dodaj pierwszy, skopiuj linki do rolki i opisu YT.
        </p>
      )}
      <div className="grid gap-4">
        {rows.map((row) => (
          <FunnelRow key={row.id} row={row} />
        ))}
      </div>
    </div>
  );
}
